(ns ui.story-publish.page
  "Replicant page for previewing and publishing/scheduling a story to
   Instagram accounts. Accessible at `/ui/story-publish?eid=<eid>`."
  (:require [parts.replicant.query :as query]
            [parts.replicant.command :as command]
            [ui.app-frame :as app-frame]
            [storrito.date.datetime-local :as datetime-local]
            [storrito.date.format :as f]
            [storrito.state.local-storage :as local-storage]))

(def user-session-query
  {:query/kind :query/user-session})

(defn story-publish-query
  [state]
  (let [eid (::story-media-eid state)]
    (when eid
      {:query/kind :query/story-publish
       :query/refresh? true
       :query/data {:story-media/eid eid}})))

(defn- publish-command
  [state]
  (let [eid (::story-media-eid state)
        selected (::selected-accounts state)
        date-str (::schedule-date-str state)
        date (when (seq date-str) (js/Date. date-str))]
    {:command/kind :command/story-publish
     :command/data
     (cond-> {:story-media/eid eid
              :multi-post (mapv (fn [entry]
                                  (cond-> {:instagram.user/id (:instagram.user/id entry)
                                           :share-to-fb? (boolean (:share-to-fb? entry))}
                                    (:share-to-fb? entry)
                                    (merge (select-keys entry
                                                        [:facebook-linkage/destination
                                                         :facebook-linkage/type]))
                                    (not-empty (:highlight-id entry))
                                    (assoc :highlight-id (:highlight-id entry))))
                                selected)}
       date (assoc :story-post/date date))}))

(defn- toggle-account
  [selected user]
  (let [user-id (:instagram.user/id user)]
    (if (some #(= (:instagram.user/id %) user-id) selected)
      (vec (remove #(= (:instagram.user/id %) user-id) selected))
      (conj (vec selected)
            {:instagram.user/id user-id
             :instagram.user/name (:instagram.user/name user)
             :share-to-fb? false}))))

(defn- toggle-fb-destination
  [selected user-id fb-linkage]
  (mapv (fn [entry]
          (if (= (:instagram.user/id entry) user-id)
            (let [dest-id (str (:facebook-linkage/id fb-linkage))
                  currently-selected (and (:share-to-fb? entry)
                                          (= (:facebook-linkage/destination entry) dest-id))]
              (if currently-selected
                (dissoc (assoc entry :share-to-fb? false)
                        :facebook-linkage/destination
                        :facebook-linkage/type)
                (assoc entry
                       :share-to-fb? true
                       :facebook-linkage/destination dest-id
                       :facebook-linkage/type (:facebook-linkage/type fb-linkage))))
            entry))
        selected))

(defn- set-highlight-id
  [selected user-id highlight-id]
  (mapv (fn [entry]
          (if (= (:instagram.user/id entry) user-id)
            (if (seq highlight-id)
              (assoc entry :highlight-id highlight-id)
              (dissoc entry :highlight-id))
            entry))
        selected))

(defn- highlight-selector
  [user-id highlights selected]
  (when (seq highlights)
    (let [entry (some #(when (= (:instagram.user/id %) user-id) %) selected)]
      [:div
       {:class ["mt-1" "pl-3" "border-l-2" "border-gray-200"]}
       [:div {:class ["text-xs" "text-gray-500" "mb-1"]} "Highlight:"]
       [:select
        {:class ["w-full" "text-xs" "border" "border-gray-300" "rounded"
                 "px-2" "py-1" "bg-white"]
         :value (or (:highlight-id entry) "")
         :on {:change [[:story-publish/set-highlight user-id :event/target.value]]}}
        [:option {:value ""} "-"]
        (map (fn [h]
               [:option {:value (:highlight/id h)
                         :replicant/key (:highlight/id h)}
                (:highlight/title h)])
             highlights)]])))

(defn- user-id->name
  "Resolves an Instagram user ID to its username via the users list."
  [users user-id]
  (or (some (fn [u]
              (when (= (:instagram.user/id u) user-id)
                (:instagram.user/name u)))
            users)
      user-id))

(defn- preview-section
  [result]
  (let [preview-url (:story-media/preview-url result)
        video-url (:story-media/video-url result)
        media-type (:story-media/type result)]
    [:div
     {:class ["w-full" "sm:w-48" "md:w-56" "shrink-0"]}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900"
               "shadow-lg"]
       :style {:aspect-ratio "9/16"}}
      (cond
        (and video-url (#{:type.instagram/video :type.instagram/animation} media-type))
        [:video
         {:src video-url
          :poster preview-url
          :controls true
          :class ["w-full" "h-full" "object-cover"]}]

        preview-url
        [:img
         {:src preview-url
          :class ["w-full" "h-full" "object-cover"]}]

        :else
        [:div
         {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                  "text-gray-500"]}
         "No preview available"])]]))

(defn- fb-crosspost-section
  [user-id facebook-linkages selected]
  (let [entry (some #(when (= (:instagram.user/id %) user-id) %) selected)]
    (when (seq facebook-linkages)
      [:div
       {:class ["mt-1" "pl-3" "border-l-2" "border-gray-200"]}
       [:div
        {:class ["text-xs" "text-gray-500" "mb-1"]}
        "Share to Facebook:"]
       [:div
        {:class ["space-y-1"]}
        (map
          (fn [linkage]
            (let [dest-id (str (:facebook-linkage/id linkage))
                  checked (and (:share-to-fb? entry)
                               (= (:facebook-linkage/destination entry) dest-id))]
              [:label
               {:class ["flex" "items-center" "gap-2" "text-xs" "text-gray-700"
                         "cursor-pointer" "py-0.5"]
                :replicant/key dest-id}
               [:input
                {:type "checkbox"
                 :checked checked
                 :class ["h-3.5" "w-3.5" "text-indigo-600" "border-gray-300"
                          "rounded" "cursor-pointer"]
                 :on {:click [[:store/assoc ::selected-accounts
                               (toggle-fb-destination selected user-id linkage)]]}}]
               [:span (:facebook-linkage/name linkage)]
               [:a
                {:href (str "https://facebook.com/" (:facebook-linkage/id linkage))
                 :target "_blank"
                 :class ["text-blue-500" "hover:text-blue-600" "underline"]}
                "link"]]))
          facebook-linkages)]])))

(defn- checkmark-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "white" :class "w-3 h-3"}
   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
           :d "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"}]])

(defn- dropdown-chevron-icon
  [open?]
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "currentColor"
         :class ["w-4" "h-4" "text-gray-400" "transition-transform"
                 (when open? "rotate-180")]}
   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
           :d "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"}]])

(defn- account-selector
  [state users highlights-by-user]
  (let [selected (or (::selected-accounts state) [])
        dropdown-open (::account-dropdown-open state)]
    [:div
     {:class ["space-y-3"]}
     [:h3
      {:class ["text-sm" "font-semibold" "text-gray-700"]}
      "Instagram Accounts"]
     (if (seq users)
       (let [all-disconnected (every? :instagram.account/not-connected users)]
         [:div
          {:class ["space-y-2"]}
          (when all-disconnected
            [:div
             {:class ["flex" "items-center" "gap-3" "px-4" "py-3" "rounded-lg"
                      "bg-amber-50" "border" "border-amber-200" "mb-2"]}
             [:div
              {:class ["flex-1" "min-w-0"]}
              [:p
               {:class ["text-sm" "font-medium" "text-amber-800"]}
               "All accounts are disconnected."]
              [:p
               {:class ["text-xs" "text-amber-600" "mt-0.5"]}
               "Reconnect at least one account to publish."]]
             [:a
              {:href "/ui/instagram-connect"
               :class ["shrink-0" "inline-flex" "items-center" "px-3" "py-1.5"
                       "text-xs" "font-medium" "text-amber-800" "bg-amber-100"
                       "hover:bg-amber-200" "rounded-md" "transition-colors"
                       "border" "border-amber-300"]}
              "Reconnect"]])
          ;; Compact dropdown trigger
          [:div
           {:class ["relative"]}
           [:button
            {:class ["w-full" "flex" "items-center" "justify-between"
                     "px-3" "py-2.5" "text-sm" "border" "border-gray-300"
                     "rounded-lg" "bg-white" "hover:bg-gray-50"
                     "cursor-pointer" "transition-colors"]
             :on {:click [[:store/assoc ::account-dropdown-open (not dropdown-open)]]}}
            [:div
             {:class ["flex" "items-center" "gap-2" "flex-1" "min-w-0"]}
             (if (seq selected)
               [:div
                {:class ["flex" "items-center" "gap-1.5" "flex-wrap"]}
                ;; Show small avatars of selected accounts
                (map
                  (fn [entry]
                    (let [user-id (:instagram.user/id entry)
                          user (some #(when (= (:instagram.user/id %) user-id) %)
                                     users)]
                      [:div
                       {:class ["flex" "items-center" "gap-1" "bg-indigo-50"
                                "border" "border-indigo-200" "rounded-full"
                                "pl-0.5" "pr-1" "py-0.5"]
                        :replicant/key user-id}
                       [:div
                        {:class ["w-5" "h-5" "rounded-full" "overflow-hidden"
                                 "bg-gray-200" "shrink-0"]}
                        (if (:instagram.user/profile-picture-url user)
                          [:img {:src (:instagram.user/profile-picture-url user)
                                 :class ["w-full" "h-full" "object-cover"]}]
                          [:div {:class ["w-full" "h-full" "flex" "items-center"
                                         "justify-center" "text-gray-400"
                                         "text-[9px]" "font-bold"]}
                           (-> (:instagram.user/name user) first str .toUpperCase)])]
                       [:span
                        {:class ["text-xs" "text-indigo-700" "truncate" "max-w-[100px]"]}
                        (:instagram.user/name user)]
                       [:span
                        {:class ["ml-0.5" "w-4" "h-4" "flex" "items-center"
                                 "justify-center" "rounded-full" "text-indigo-400"
                                 "hover:bg-indigo-200" "hover:text-indigo-700"
                                 "cursor-pointer" "leading-none" "text-sm"]
                         :title (str "Remove " (:instagram.user/name user))
                         :on {:click [[:js/stop-propagation]
                                      [:store/assoc ::selected-accounts
                                       (vec (remove #(= (:instagram.user/id %) user-id)
                                                    selected))]]}}
                        "×"]]))
                  selected)]
               [:span
                {:class ["text-gray-400"]}
                "Select accounts..."])]
            (dropdown-chevron-icon dropdown-open)]
           ;; Backdrop closes the dropdown on outside click
           (when dropdown-open
             [:div
              {:class ["fixed" "inset-0" "z-0"]
               :on {:click [[:store/assoc ::account-dropdown-open false]]}}])
           ;; Dropdown panel
           (when dropdown-open
             [:div
              {:class ["absolute" "z-10" "mt-1" "w-full" "bg-white" "border"
                       "border-gray-200" "rounded-lg" "shadow-lg"
                       "max-h-60" "overflow-y-auto"]}
              (map
                (fn [user]
                  (let [user-id (:instagram.user/id user)
                        is-selected (some #(= (:instagram.user/id %) user-id) selected)
                        not-connected (:instagram.account/not-connected user)]
                    [:div
                     {:class (into ["flex" "items-center" "gap-2.5" "px-3" "py-2"
                                    "cursor-pointer" "transition-colors"]
                                   (cond
                                     not-connected ["opacity-50" "cursor-default"]
                                     is-selected ["bg-indigo-50"]
                                     :else ["hover:bg-gray-50"]))
                      :replicant/key user-id
                      :on {:click (when-not not-connected
                                    (cond-> [[:store/assoc ::selected-accounts
                                              (toggle-account selected user)]]
                                      (not is-selected)
                                      (conj [:ig/save-selection user-id])))}}
                     [:div
                      {:class ["w-7" "h-7" "rounded-full" "overflow-hidden"
                               "bg-gray-200" "shrink-0"]}
                      (if (:instagram.user/profile-picture-url user)
                        [:img {:src (:instagram.user/profile-picture-url user)
                               :class ["w-full" "h-full" "object-cover"]}]
                        [:div {:class ["w-full" "h-full" "flex" "items-center"
                                       "justify-center" "text-gray-400"
                                       "text-xs" "font-bold"]}
                         (-> (:instagram.user/name user) first str .toUpperCase)])]
                     [:div
                      {:class ["flex-1" "min-w-0"]}
                      [:div
                       {:class ["text-sm" "text-gray-900" "truncate"]}
                       (:instagram.user/name user)]
                      (when not-connected
                        [:div
                         {:class ["text-xs" "text-red-500"]}
                         "Disconnected"])]
                     [:div
                      {:class (into ["w-4" "h-4" "rounded" "border-2" "flex"
                                     "items-center" "justify-center" "shrink-0"]
                                    (if is-selected
                                      ["border-indigo-500" "bg-indigo-500"]
                                      ["border-gray-300"]))}
                      (when is-selected
                        (checkmark-icon))]]))
                users)])]
          ;; Expanded settings for selected accounts (FB crosspost, highlights)
          (when (seq selected)
            [:div
             {:class ["space-y-2"]}
             (map
               (fn [entry]
                 (let [user-id (:instagram.user/id entry)
                       user (some #(when (= (:instagram.user/id %) user-id) %) users)
                       fb-linkages (:instagram.user/facebook-linkages user)
                       highlights (get highlights-by-user user-id)]
                   (when (or (seq fb-linkages) (seq highlights))
                     [:div
                      {:class ["p-2.5" "rounded-lg" "border" "border-gray-100"
                               "bg-gray-50"]
                       :replicant/key (str "settings-" user-id)}
                      [:div
                       {:class ["text-xs" "font-medium" "text-gray-600" "mb-1"]}
                       (str "@" (:instagram.user/name user))]
                      (when (seq fb-linkages)
                        (fb-crosspost-section user-id fb-linkages selected))
                      (when (seq highlights)
                        (highlight-selector user-id highlights selected))])))
               selected)])])
       [:div
        {:class ["text-sm" "text-gray-500" "p-4" "text-center"
                 "bg-gray-50" "rounded-lg" "border" "border-gray-200"]}
        "No Instagram accounts connected."])
     [:button
      {:class ["inline-flex" "items-center" "gap-1.5" "text-sm" "text-indigo-600"
               "hover:text-indigo-700" "font-medium" "cursor-pointer"]
       :on {:click [[:connect3-info/open {:source "story-publish"}]]}}
      "+ Add account"]]))

(defn- older-than-1h?
  [post]
  (when-let [date (:instagram.story.post/date post)]
    (> (- (.getTime (js/Date.)) (.getTime date))
       (* 60 60 1000))))

(defn- future-credit-warning
  [result]
  [:div
   {:class ["p-3" "rounded-lg" "bg-blue-50" "border" "border-blue-200"]}
   [:p
    {:class ["text-sm" "text-blue-800"]}
    "Credits are consumed by usage over time. Scheduled stories only publish if your credit balance is positive at the scheduled time. "
    (when-let [balance (:credit-balance-formatted result)]
      [:span
       "Current balance: "
       [:span {:class ["font-medium"]} balance]
       ". "])
    [:a
     {:href "/ui/subscription?source=publish-flow"
      :class ["font-medium" "text-blue-900" "underline"]}
     "Add credits"]
    " if you plan to schedule far ahead."]])

(defn- schedule-section
  [state result]
  (let [selected (or (::selected-accounts state) [])
        date-str (::schedule-date-str state)
        is-future (datetime-local/date-in-future? date-str)
        q (story-publish-query state)
        cmd (publish-command state)
        history (:story-publish/history result)
        new-posts-settled
        (let [prev-count (or (::published-history-count state) 0)]
          (and (> (count history) prev-count)
               (every? (fn [post]
                         (or (:instagram.story.post/executed post)
                             (:instagram.story.post/error-message post)
                             (older-than-1h? post)
                             (and (not (:instagram.story.post/started post))
                                  (when-let [date (:instagram.story.post/date post)]
                                    (> (.getTime date) (.getTime (js/Date.)))))))
                       history)))
        published (and (or (::published state)
                           (command/success? state cmd))
                       (not new-posts-settled))
        trial-remaining (or (:organization/trial-posts-remaining result) 0)
        can-publish (and (seq selected)
                         (not published)
                         (or (:organization/positive-credit-balance? result)
                             (:organization/legacy-customer? result)
                             (pos? trial-remaining)))]
    [:div
     {:class ["space-y-4"]}
     [:h3
      {:class ["text-sm" "font-semibold" "text-gray-700"]}
      "Date & Time"]

     [:input
      {:type "datetime-local"
       :value (or date-str (datetime-local/current-datetime-local))
       :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
               "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
               "focus:border-indigo-500"]
       :on {:input [[:store/assoc ::schedule-date-str :event/target.value]
                    [:store/assoc ::published nil]]}}]

     (when (and is-future
                (not published)
                (:organization/positive-credit-balance? result)
                (not (:organization/legacy-customer? result))
                (not (:organization/free-account? result)))
       (future-credit-warning result))

     (when (and (not (:organization/positive-credit-balance? result))
                (not (:organization/legacy-customer? result))
                (not (:organization/free-account? result)))
       [:div
        {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
        [:p
         {:class ["text-sm" "text-amber-800"]}
         "Your credit balance is empty. "
         [:button
          {:class ["font-medium" "text-amber-900" "underline" "cursor-pointer"]
           :on {:click [[:data/query {:query/kind :query/user-session}]
                        [:ui/modal-open :modals/credits-exhausted {}]]}}
          "Top up credits"]
         " to publish stories."]])

     (when (and (:organization/free-account? result)
                (not published))
       (if (pos? trial-remaining)
         [:div
          {:class ["p-3" "rounded-lg" "bg-gray-50" "border" "border-gray-200"]}
          [:p
           {:class ["text-sm" "text-gray-600"]}
           (str "Legacy posting allowance: " trial-remaining " story "
                (if (= 1 trial-remaining) "post" "posts")
                " remaining")]]
         [:div
          {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
          [:p
           {:class ["text-sm" "text-amber-800"]}
           "Your legacy posting allowance is used up. "
           [:a
            {:href "/ui/subscription?source=publish-flow"
             :class ["font-medium" "text-amber-900" "underline"]}
            "Add credits to keep posting"]]]))

     (when published
       [:div
        {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
        [:p
         {:class ["text-sm" "text-green-800" "font-medium"]}
         (if is-future
           "Story scheduled successfully!"
           "Story is being posted!")]])

     (when (command/error? state cmd)
       [:div
        {:class ["p-3" "rounded-lg" "bg-red-50" "border" "border-red-200"]}
        [:p
         {:class ["text-sm" "text-red-800"]}
         "Failed to publish story. Please try again."]])

     [:button
      {:class (into ["w-full" "py-2.5" "px-4" "text-sm" "font-semibold" "rounded-lg"
                     "transition-colors" "cursor-pointer"]
                    (if can-publish
                      ["bg-indigo-600" "text-white" "hover:bg-indigo-700"]
                      ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
       :disabled (or (not can-publish)
                     (command/issued? state cmd))
       :on {:click (when can-publish
                     [[:store/assoc ::published-history-count (count history)]
                      [:data/command
                       cmd
                       {:on-success [[:store/assoc ::published true]
                                     [:data/query q]]
                        :on-error [[:ui/show-error]]}]])}}
      (cond
        (command/issued? state cmd) "Publishing..."
        published (if is-future "Scheduled" "Posted")
        is-future "Schedule"
        :else "Post now")]]))


(defn- history-status
  [post]
  (cond
    (:instagram.story.post/error-message post)
    [:span
     {:class ["text-xs" "text-red-700" "bg-red-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]
      :title (:instagram.story.post/error-message post)}
     "Error"]

    (:instagram.story.post/executed post)
    [:span
     {:class ["text-xs" "text-green-700" "bg-green-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]}
     "Posted"]

    (and (not (:instagram.story.post/executed post))
         (older-than-1h? post))
    [:span
     {:class ["text-xs" "text-red-700" "bg-red-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]}
     "Failed"]

    (and (:instagram.story.post/started post)
         (not (:instagram.story.post/next-retry post)))
    [:span
     {:class ["text-xs" "text-amber-700" "bg-amber-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]}
     "Posting..."]

    (:instagram.story.post/next-retry post)
    [:span
     {:class ["text-xs" "text-amber-700" "bg-amber-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]
      :title (str "Will retry at "
                  (f/format-standard-datetime (:instagram.story.post/next-retry post)))}
     "Retrying"]

    :else
    [:span
     {:class ["text-xs" "text-blue-700" "bg-blue-50" "px-2" "py-0.5"
              "rounded-full" "font-medium"]}
     "Scheduled"]))

(defn- fb-icon
  [fb-name]
  (when fb-name
    [:span
     {:class ["text-blue-500" "shrink-0"]
      :title fb-name}
     [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 320 512"
            :fill "currentColor" :class "w-3 h-3"}
      [:path {:d "M80 299.3V512H196V299.3h86.5l18-97.8H196V136.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4.4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z"}]]]))

(defn- history-section
  [state result]
  (let [history (:story-publish/history result)
        users (:instagram/users result)
        q (story-publish-query state)]
    (when (seq history)
      [:div
       {:class ["space-y-3" "mt-6"]}
       [:h3
        {:class ["text-sm" "font-semibold" "text-gray-700"]}
        "Post History"]
       [:div
        {:class ["bg-white" "rounded-lg" "border" "border-gray-200"
                 "overflow-hidden"]}
        [:table
         {:class ["w-full" "text-xs"]}
         [:thead
          [:tr
           {:class ["border-b" "border-gray-100" "text-left" "text-gray-500"]}
           [:th {:class ["py-2" "px-4" "font-medium"]} "Status"]
           [:th {:class ["py-2" "px-4" "font-medium"]} "Account"]
           [:th {:class ["py-2" "px-4" "font-medium" "hidden" "sm:table-cell"]} "Date"]
           [:th {:class ["py-2" "px-4" "font-medium" "w-8"]} ""]
           [:th {:class ["py-2" "px-4" "font-medium" "w-16"]} ""]]]
         [:tbody
          {:class ["divide-y" "divide-gray-50"]}
          (map
            (fn [post]
              [:tr
               {:class ["hover:bg-gray-50"]
                :replicant/key (:db/id post)}
               [:td {:class ["py-2.5" "px-4"]}
                (history-status post)]
               [:td {:class ["py-2.5" "px-4" "text-gray-600" "font-medium"]}
                (str "@" (user-id->name users (:instagram.story.post/user-id post)))]
               [:td {:class ["py-2.5" "px-4" "text-gray-400" "hidden" "sm:table-cell"]}
                (f/format-standard-datetime (:instagram.story.post/date post))]
               [:td {:class ["py-2.5" "px-4"]}
                (fb-icon (:instagram.story.post/fb-name post))]
               [:td {:class ["py-2.5" "px-4" "text-right"]}
                (when (:story-post/cancel-command post)
                  [:button
                   {:class ["text-xs" "text-red-600" "hover:text-red-700"
                            "font-medium" "cursor-pointer"]
                    :on {:click [[:js/confirm "Cancel this scheduled post?"]
                                 [:data/command
                                  {:command/kind :command/story-post-cancel
                                   :command/data {:db/id (:db/id post)}}
                                  {:on-success [[:data/query q]
                                                [:store/assoc ::published nil]
                                                [:store/assoc ::published-history-count nil]]
                                   :on-error [[:ui/show-error]]}]]}}
                   "Cancel"])]])
            history)]]]])))

(defn- auto-select-first-account
  "Returns initial selected-accounts with the previously used user
   pre-selected (persisted in localStorage). Falls back to the first
   connected user when no saved selection exists."
  [users saved-ig-user-id]
  (let [connected (remove :instagram.account/not-connected users)
        preferred (or (when saved-ig-user-id
                        (some #(when (= (:instagram.user/id %) saved-ig-user-id) %)
                              connected))
                      (first connected))]
    (when preferred
      [{:instagram.user/id (:instagram.user/id preferred)
        :instagram.user/name (:instagram.user/name preferred)
        :share-to-fb? false}])))

(defn render
  [state]
  (let [q (story-publish-query state)
        result (when q (query/get-result state q))
        loading (or (not q)
                    (and (query/loading? state q)
                         (not (query/available? state q)))
                    (and (query/loading? state user-session-query)
                         (not (query/available? state user-session-query))))
        ;; Auto-select first account when none selected and users are loaded
        state (if (and (nil? (::selected-accounts state))
                       (seq (:instagram/users result)))
                (assoc state ::selected-accounts
                       (auto-select-first-account
                         (:instagram/users result)
                         (::saved-ig-user-id state)))
                state)]
    (if loading
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]
      (app-frame/app-frame
        state
        [:div
         {:class ["max-w-4xl" "mx-auto" "overflow-x-hidden"]}
         [:div
          {:class ["mb-4"]}
          [:h1
           {:class ["text-xl" "font-bold" "text-gray-900"]}
           (or (:story-media/name result) "Story")]
          [:p
           {:class ["text-sm" "text-gray-500" "mt-1"]}
           [:a
            {:href "/ui/agenda"
             :class ["text-indigo-600" "hover:text-indigo-700"]}
            "Back to schedule"]]]
         [:div
          {:class ["flex" "flex-col" "sm:flex-row" "gap-6"]}
          (preview-section result)
          [:div
           {:class ["flex-1" "space-y-6"]}
           (account-selector state (:instagram/users result)
                            (:story-publish/highlights result))
           (schedule-section state result)]]
         (history-section state result)]))))

(defn on-load
  [location]
  (let [eid-str (get-in location [:location/query-params :eid])
        eid (when eid-str
              (let [n (js/parseInt eid-str 10)]
                (when-not (js/isNaN n) n)))
        saved-ig-user-id (:instagram.user/id @local-storage/state)]
    (when eid
      (cond-> [[:ui/hide-sidebar-on-mobile]
               [:store/assoc ::story-media-eid eid]
               [:store/assoc ::published nil]
               [:store/assoc ::published-history-count nil]
               [:store/assoc ::schedule-date-str nil]
               [:data/query (story-publish-query {::story-media-eid eid})]
               [:data/query {:query/kind :query/user-session}]]
        saved-ig-user-id
        (conj [:store/assoc ::saved-ig-user-id saved-ig-user-id])))))

(def page
  {:page-id :pages/story-publish
   :route [["ui" "story-publish"]]
   :on-load on-load
   :render #'render})
